import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { patientApi } from '../api/patientApi'
import type { Patient } from '../types/patient'

export function useGetPatients(
  params?: {
    hn?: string
    cardId?: string
    name?: string
  },
  enabled = true,
) {
  return useQuery({
    queryKey: ['patients', params],
    queryFn: () => patientApi.getPatients(params),
    retry: false,
    refetchOnWindowFocus: false,
    enabled,
  })
}

export function useGetPatient(id: string, enabled = true) {
  return useQuery({
    queryKey: ['patient', id],
    queryFn: () => patientApi.getPatient(id),
    enabled: enabled && !!id,
  })
}

export function useCreatePatient() {
  const queryClient = useQueryClient()

  return useMutation<Patient, Error, any>({
    mutationFn: patientApi.createPatient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] })
    },
  })
}

export function useUpdatePatient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<Patient> }) => patientApi.updatePatient(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] })
    },
  })
}

export function useDeletePatient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => patientApi.deletePatient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] })
    },
  })
}

export function useGetPatientItems(id: string, enabled = true) {
  return useQuery({
    queryKey: ['patient-items', id],
    queryFn: () => patientApi.getPatientItems(id),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: enabled && !!id,
  })
}
